import {
  faTimes,
  faSpinner,
  faSearch
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/base/Button';
import { useCallback, useState } from 'react';
import { FloatingLabel, Form, Modal, Spinner } from 'react-bootstrap';
import {
  searchFoods,
  getFoodDetails,
  FoodSearchResult,
  FoodDetails,
  NutritionFact
} from 'services/foodSearchService';

import { trackEvent } from '../../../../utils/analytics';

interface AddFoodItemModalProps {
  show: boolean;
  onClose: () => void;
  onAddItem: (item: {
    foodItemId: string;
    name: string;
    name_ar?: string | null;
    brand_name_en?: string | null;
    brand_name_ar?: string | null;
    quantity: string;
    servingSize: number;
    servingSizeUnit: string;
    nutritionFact: NutritionFact[];
  }) => Promise<void>;
  mealId: string;
}

type Stage = 'search' | 'confirm';

const AddFoodItemModal = ({
  show,
  onClose,
  onAddItem,
  mealId
}: AddFoodItemModalProps) => {
  const [stage, setStage] = useState<Stage>('search');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<FoodSearchResult[]>([]);
  const [selectedFood, setSelectedFood] = useState<FoodDetails | null>(null);
  const [servingSize, setServingSize] = useState(0);
  const [servingSizeUnit, setServingSizeUnit] = useState('g');
  const [selectedPortionIndex, setSelectedPortionIndex] = useState<
    number | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addingItem, setAddingItem] = useState(false);
  const [searchAttempted, setSearchAttempted] = useState(false);

  const handleSearch = useCallback(async () => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      setError(null);
      setSearchAttempted(false);
      return;
    }

    try {
      setSearchAttempted(true);
      setSearchLoading(true);
      setError(null);
      const results = await searchFoods(searchTerm);
      setSearchResults(results);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to search foods');
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [searchTerm]);

  const handleSelectFood = useCallback(async (fdcId: string) => {
    try {
      setLoading(true);
      setError(null);
      const details = await getFoodDetails(fdcId);
      setSelectedFood(details);

      // Auto-select first portion if available
      if (details.portions && details.portions.length > 0) {
        const sortedPortions = [...details.portions].sort(
          (a, b) => a.gramWeight - b.gramWeight
        );
        const firstPortion = sortedPortions[0];
        const firstIndex = details.portions.indexOf(firstPortion);
        setSelectedPortionIndex(firstIndex);
        setServingSize(firstPortion.gramWeight);
        setServingSizeUnit(details.servingSizeUnit || 'g');
      } else {
        setSelectedPortionIndex(null);
        setServingSize(0);
        setServingSizeUnit('g');
      }

      setStage('confirm');
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load food details'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAddToMeal = useCallback(async () => {
    if (!selectedFood) return;

    try {
      setAddingItem(true);
      setError(null);

      await onAddItem({
        foodItemId: selectedFood.foodItemId,
        name: selectedFood.description,
        name_ar: selectedFood.name_ar,
        brand_name_en: selectedFood.brand_name_en,
        brand_name_ar: selectedFood.brand_name_ar,
        quantity: servingSize.toString(),
        servingSize,
        servingSizeUnit,
        nutritionFact: selectedFood.nutritionFacts
      });

      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

      trackEvent('meal_item_added', {
        meal_id: String(mealId),
        actor_user_id: String(currentUser.id || ''),
        actor_user_name: `${currentUser.firstname || ''} ${
          currentUser.lastname || ''
        }`.trim(),
        actor_role:
          currentUser.role?.toLowerCase() === 'clinic'
            ? 'clinic admin'
            : currentUser.role || '',
        platform: 'web'
      });

      // Reset and close
      resetModal();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add food item');
    } finally {
      setAddingItem(false);
    }
  }, [selectedFood, servingSize, servingSizeUnit, onAddItem, onClose]);

  const handleBack = () => {
    setStage('search');
    setSelectedFood(null);
    setError(null);
  };

  const resetModal = () => {
    setStage('search');
    setSearchTerm('');
    setSearchResults([]);
    setSelectedFood(null);
    setServingSize(0);
    setServingSizeUnit('g');
    setSelectedPortionIndex(null);
    setError(null);
    setSearchAttempted(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      contentClassName="border"
      backdrop="static"
      style={{ zIndex: 2000 }}
    >
      <Modal.Header className="ps-card border-bottom border-translucent">
        <Modal.Title className="modal-title text-body-highlight mb-0">
          {stage === 'search'
            ? 'Search and Add Food Item'
            : 'Confirm Food Item Details'}
        </Modal.Title>
        <Button className="p-1" onClick={handleClose}>
          <FontAwesomeIcon icon={faTimes} className="fs-8" />
        </Button>
      </Modal.Header>

      <Modal.Body className="px-card pb-card pt-card fs-9">
        {error && (
          <div
            className="alert alert-danger alert-dismissible fade show"
            role="alert"
          >
            {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label="Close"
            ></button>
          </div>
        )}

        {stage === 'search' ? (
          // Stage 1: Search
          <div>
            <div className="mb-4">
              <label className="form-label fw-bold mb-2">
                Search USDA Food Database
              </label>
              <Form
                onSubmit={e => {
                  e.preventDefault();
                  handleSearch();
                }}
              >
                <div className="d-flex gap-2 align-items-start">
                  <FloatingLabel
                    controlId="foodSearch"
                    label="Search for food"
                    className="flex-grow-1"
                  >
                    <Form.Control
                      type="text"
                      placeholder="Search food items..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      disabled={searchLoading}
                      className="form-control"
                      autoFocus
                    />
                  </FloatingLabel>

                  <Button
                    type="submit"
                    variant="primary"
                    disabled={searchLoading}
                    style={{ height: '50px' }}
                  >
                    {searchLoading ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Searching...
                      </>
                    ) : (
                      'Search'
                    )}
                  </Button>
                </div>
              </Form>
            </div>

            {searchResults.length > 0 && (
              <div>
                <label className="form-label text-body-secondary">
                  Select a food item
                </label>
                <div
                  className="list-group"
                  style={{ maxHeight: '300px', overflowY: 'auto' }}
                >
                  {searchResults.map(food => (
                    <button
                      key={food.fdcId}
                      type="button"
                      className="list-group-item list-group-item-action"
                      onClick={() => handleSelectFood(food.fdcId)}
                      disabled={loading}
                    >
                      <div className="d-flex w-100 justify-content-between align-items-start">
                        <div className="text-start">
                          <h6 className="mb-0">{food.description}</h6>
                          <small className="text-muted">ID: {food.fdcId}</small>
                        </div>
                        {loading && (
                          <Spinner
                            animation="border"
                            size="sm"
                            className="text-primary"
                          />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {searchAttempted &&
              searchResults.length === 0 &&
              !searchLoading && (
                <div className="alert alert-info">
                  No foods found matching "{searchTerm}". Try a different search
                  term.
                </div>
              )}
          </div>
        ) : (
          // Stage 2: Confirm Details
          <div>
            <div className="mb-3 p-3 bg-light rounded">
              <h6 className="text-body-highlight mb-2">Selected Food Item</h6>
              <p className="mb-0 fw-bold">{selectedFood?.description}</p>
            </div>

            <div className="row mb-3">
              <div className="col-md-12">
                <FloatingLabel controlId="portion" label="Serving Size">
                  <Form.Select
                    value={selectedPortionIndex ?? ''}
                    onChange={e => {
                      const index = Number(e.target.value);
                      setSelectedPortionIndex(index);
                      const portion = selectedFood?.portions?.[index];
                      if (portion) {
                        setServingSize(portion.gramWeight);
                        setServingSizeUnit(
                          selectedFood?.servingSizeUnit || 'g'
                        );
                      }
                    }}
                    disabled={addingItem || !selectedFood?.portions?.length}
                  >
                    {selectedFood?.portions?.length ? (
                      selectedFood.portions
                        .map((portion, index) => ({ portion, index }))
                        .sort(
                          (a: any, b: any) =>
                            a.portion.gramWeight - b.portion.gramWeight
                        )
                        .map(({ portion, index }) => (
                          <option
                            key={`${portion.portionDescription}-${index}`}
                            value={index}
                          >
                            {portion.portionDescription} ({portion.gramWeight}{' '}
                            g)
                          </option>
                        ))
                    ) : (
                      <option value="">No portion data available</option>
                    )}
                  </Form.Select>
                </FloatingLabel>
                {selectedFood?.portions?.length ? (
                  <div className="form-text">
                    Selected: {servingSize} {servingSizeUnit}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="mb-3 p-3 bg-light rounded border border-success">
              <h6 className="text-body-highlight mb-3 d-flex align-items-center">
                <span className="badge bg-success me-2">✓</span>
                Nutrition Facts (per {servingSize}g)
              </h6>
              <ul className="list-unstyled">
                {selectedFood?.nutritionFacts.map((fact, idx) => {
                  const multiplier = servingSize / 100;
                  const adjustedValue = parseFloat(
                    (fact.value * multiplier).toFixed(2)
                  );
                  return (
                    <li
                      key={idx}
                      className="mb-2 d-flex justify-content-between align-items-center"
                    >
                      <span className="text-body-secondary">
                        {fact.nutrientName}:
                      </span>
                      <span className="badge bg-info text-dark">
                        {adjustedValue} {fact.unitName}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="px-card pb-card border-top border-translucent d-flex justify-content-end gap-2">
        {stage === 'search' && !addingItem && (
          <Button
            variant="danger"
            onClick={handleClose}
            title="Cancel food selection"
          >
            Cancel
          </Button>
        )}
        {stage === 'confirm' && (
          <Button
            variant="secondary"
            onClick={handleBack}
            disabled={addingItem}
            title="Go back to food search"
          >
            ← Back
          </Button>
        )}
        {stage === 'confirm' && (
          <Button
            variant="primary"
            onClick={handleAddToMeal}
            disabled={addingItem || !selectedFood}
            title={
              !selectedFood
                ? 'Please select a food item'
                : 'Add selected food to meal'
            }
          >
            {addingItem ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Adding...
              </>
            ) : (
              '✓ Add to Meal'
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default AddFoodItemModal;
